import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Switch, App, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import api from '../api/client';

const LEVELS = ['PSLE', 'O-Level', 'IP', 'JC', 'IB'];

export default function AdminSubjects() {
  const { message } = App.useApp();
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.get('/api/admin/subjects')
      .then(r => setRows(r.data))
      .catch(() => message.error('Failed to load subjects'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, level: 'O-Level' });
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue(row);
    setModal(true);
  };

  const save = async (values) => {
    try {
      if (editing) {
        await api.patch(`/api/admin/subjects/${editing.subject_key}`, values);
        message.success('Subject updated');
      } else {
        await api.post('/api/admin/subjects', values);
        message.success('Subject created');
      }
      setModal(false);
      load();
    } catch (e) {
      message.error(e.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (key) => {
    try {
      await api.delete(`/api/admin/subjects/${key}`);
      message.success('Subject deleted');
      load();
    } catch (e) {
      message.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const columns = [
    { title: 'Key', dataIndex: 'subject_key', width: 200,
      render: v => <code style={{ fontSize: 12, background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{v}</code> },
    { title: 'Display name', dataIndex: 'display_name' },
    { title: 'Level', dataIndex: 'level', width: 110,
      render: v => <Tag>{v}</Tag> },
    { title: 'Curriculum', dataIndex: 'curriculum', width: 150,
      render: v => v || <span style={{ color: '#bbb' }}>—</span> },
    { title: 'Active', dataIndex: 'is_active', width: 90,
      render: v => v ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag> },
    { title: '', width: 100,
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this subject?" onConfirm={() => remove(row.subject_key)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Subjects</h1>
          <p style={{ color: '#888', marginTop: 4, marginBottom: 0 }}>
            The master list of subjects tutors can pick from.
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />}
          style={{ background: '#0a0a0a', border: 'none' }}
          onClick={openCreate}>
          Add subject
        </Button>
      </div>

      <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="subject_key"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>

      <Modal
        title={editing ? `Edit ${editing.display_name}` : 'Add subject'}
        open={modal}
        maskClosable={false}
        onCancel={() => setModal(false)}
        onOk={() => form.submit()}
        okText={editing ? 'Save' : 'Create'}
        okButtonProps={{ style: { background: '#0a0a0a', border: 'none' } }}
      >
        <Form form={form} layout="vertical" onFinish={save} requiredMark={false} style={{ marginTop: 16 }}>
          <Form.Item label="Subject key (unique identifier — lowercase, no spaces)"
            name="subject_key" rules={[{ required: !editing, pattern: /^[a-z0-9_]+$/,
              message: 'Lowercase letters, digits, underscores only' }]}>
            <Input placeholder="e.g. pure_chem" disabled={!!editing} />
          </Form.Item>
          <Form.Item label="Display name" name="display_name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Pure Chemistry (5072)" />
          </Form.Item>
          <Form.Item label="Level" name="level" rules={[{ required: true }]}>
            <Select options={LEVELS.map(l => ({ value: l, label: l }))} />
          </Form.Item>
          <Form.Item label="Curriculum (optional)" name="curriculum">
            <Input placeholder="e.g. Singapore MOE" />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
}
